// Centralized icon registry. Extracted from the inline SVGs that used to live
// directly in Home.tsx so that data files (services.ts, etc.) can reference an
// icon by id instead of importing JSX, and so trimming Home.tsx doesn't leave
// behind unused icon components (which would fail CI's zero-warning lint).

import { ReactElement, ReactNode } from 'react';
import microsoft365 from '../assets/tools/microsoft-365.png';

export type IconId =
  | 'discovery'
  | 'advisory'
  | 'document'
  | 'support'
  | 'lead'
  | 'onboarding'
  | 'reporting'
  | 'approvals'
  | 'security'
  | 'governance'
  | 'speed'
  | 'location'
  | 'automation'
  | 'microsoft'
  | 'check'
  | 'layer-ai'
  | 'layer-automation'
  | 'layer-data'
  | 'layer-delivery'
  | 'briefcase'
  | 'heart'
  | 'factory'
  | 'stethoscope'
  | 'award'
  | 'ms-copilot'
  | 'ms-power-platform'
  | 'ms-azure'
  | 'ms-365';

interface IconProps {
  size?: number;
}

function StrokeIcon({ size = 24, children }: IconProps & { children: ReactNode }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {children}
    </svg>
  );
}

function ProductImg({ size = 48, src, alt }: IconProps & { src: string; alt: string }) {
  return <img src={src} alt={alt} width={size} height={size} style={{ objectFit: 'contain' }} />;
}

export const ICONS: Record<IconId, (props: IconProps) => ReactElement> = {
  discovery: props => (
    <StrokeIcon {...props}>
      <path d="M3 3v18h18" />
      <path d="M7 16l4-4 4 4 5-5" />
    </StrokeIcon>
  ),
  advisory: props => (
    <StrokeIcon {...props}>
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </StrokeIcon>
  ),
  document: props => (
    <StrokeIcon {...props}>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="8" y1="13" x2="14" y2="13" />
      <line x1="8" y1="17" x2="12" y2="17" />
    </StrokeIcon>
  ),
  support: props => (
    <StrokeIcon {...props}>
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
      <line x1="8" y1="9" x2="16" y2="9" />
      <line x1="8" y1="13" x2="13" y2="13" />
    </StrokeIcon>
  ),
  lead: props => (
    <StrokeIcon {...props}>
      <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
    </StrokeIcon>
  ),
  onboarding: props => (
    <StrokeIcon {...props}>
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <line x1="19" y1="8" x2="19" y2="14" />
      <line x1="22" y1="11" x2="16" y2="11" />
    </StrokeIcon>
  ),
  reporting: props => (
    <StrokeIcon {...props}>
      <path d="M21 2v6h-6" />
      <path d="M3 12a9 9 0 0 1 15-6.7L21 8" />
      <path d="M3 22v-6h6" />
      <path d="M21 12a9 9 0 0 1-15 6.7L3 16" />
    </StrokeIcon>
  ),
  approvals: props => (
    <StrokeIcon {...props}>
      <path d="M9 11l3 3L22 4" />
      <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
    </StrokeIcon>
  ),
  security: props => (
    <StrokeIcon {...props}>
      <rect x="3" y="11" width="18" height="11" rx="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </StrokeIcon>
  ),
  governance: props => (
    <StrokeIcon {...props}>
      <path d="M12 3v18M3 12h18M6 6l12 12M18 6L6 18" />
    </StrokeIcon>
  ),
  speed: props => (
    <StrokeIcon {...props}>
      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
    </StrokeIcon>
  ),
  location: props => (
    <StrokeIcon {...props}>
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
      <circle cx="12" cy="10" r="3" />
    </StrokeIcon>
  ),
  automation: props => (
    <StrokeIcon {...props}>
      <rect x="2" y="3" width="7" height="5" rx="1.5" />
      <rect x="15" y="3" width="7" height="5" rx="1.5" />
      <rect x="8.5" y="16" width="7" height="5" rx="1.5" />
      <path d="M5.5 8v3.5h13V8" />
      <path d="M12 11.5V16" />
    </StrokeIcon>
  ),
  check: props => (
    <StrokeIcon {...props}>
      <circle cx="12" cy="12" r="10" />
      <path d="M9 12l2 2 4-4" />
    </StrokeIcon>
  ),
  'layer-ai': props => (
    <StrokeIcon {...props}>
      <rect x="4" y="4" width="16" height="16" rx="2" />
      <rect x="9" y="9" width="6" height="6" />
      <path d="M9 1v3M15 1v3M9 20v3M15 20v3M20 9h3M20 14h3M1 9h3M1 14h3" />
    </StrokeIcon>
  ),
  'layer-automation': props => (
    <StrokeIcon {...props}>
      <circle cx="6" cy="6" r="2.25" />
      <circle cx="18" cy="6" r="2.25" />
      <circle cx="12" cy="18" r="2.25" />
      <path d="M6 8.25V11a4 4 0 0 0 4 4M18 8.25V11a4 4 0 0 1-4 4" />
    </StrokeIcon>
  ),
  'layer-data': props => (
    <StrokeIcon {...props}>
      <ellipse cx="12" cy="5" rx="8" ry="3" />
      <path d="M4 5v14a8 3 0 0 0 16 0V5" />
      <path d="M4 12a8 3 0 0 0 16 0" />
    </StrokeIcon>
  ),
  'layer-delivery': props => (
    <StrokeIcon {...props}>
      <rect x="4" y="3" width="16" height="18" rx="2" />
      <path d="M9 3h6v3H9z" />
      <path d="M9 12l2 2 4-4" />
    </StrokeIcon>
  ),
  briefcase: props => (
    <StrokeIcon {...props}>
      <rect x="2" y="7" width="20" height="14" rx="2" />
      <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
    </StrokeIcon>
  ),
  heart: props => (
    <StrokeIcon {...props}>
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
    </StrokeIcon>
  ),
  factory: props => (
    <StrokeIcon {...props}>
      <path d="M2 20V9l6 4V9l6 4V4h6v16z" />
      <path d="M2 20h20" />
      <path d="M17 14h1M17 17h1M6 17h1M11 17h1" />
    </StrokeIcon>
  ),
  stethoscope: props => (
    <StrokeIcon {...props}>
      <path d="M4.8 2.3A.3.3 0 1 0 5 2H4a2 2 0 0 0-2 2v5a6 6 0 0 0 6 6 6 6 0 0 0 6-6V4a2 2 0 0 0-2-2h-1a.2.2 0 1 0 .3.3" />
      <path d="M8 15v1a6 6 0 0 0 6 6 6 6 0 0 0 6-6v-4" />
      <circle cx="20" cy="10" r="2" />
    </StrokeIcon>
  ),
  award: props => (
    <StrokeIcon {...props}>
      <circle cx="12" cy="8" r="6" />
      <path d="M15.5 13.5 17 22l-5-3-5 3 1.5-8.5" />
    </StrokeIcon>
  ),
  microsoft: ({ size = 48 }) => (
    <svg width={size} height={size} viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
      <rect x="4" y="4" width="18" height="18" fill="#f25022" />
      <rect x="26" y="4" width="18" height="18" fill="#7fba00" />
      <rect x="4" y="26" width="18" height="18" fill="#00a4ef" />
      <rect x="26" y="26" width="18" height="18" fill="#ffb900" />
    </svg>
  ),
  'ms-copilot': props => <ProductImg {...props} src="/ms-copilot.png" alt="Microsoft Copilot" />,
  'ms-power-platform': props => (
    <ProductImg {...props} src="/ms-power-platform.png" alt="Microsoft Power Platform" />
  ),
  'ms-azure': props => <ProductImg {...props} src="/ms-azure.png" alt="Microsoft Azure" />,
  'ms-365': props => <ProductImg {...props} src={microsoft365} alt="Microsoft 365" />,
};

export function Icon({ id, size }: { id: IconId; size?: number }) {
  const Component = ICONS[id];
  return <Component size={size} />;
}
